/**
 * Image optimization helpers.
 *
 * - Internal `/api/images/file/*` URLs gain a `?w=NNN` query param.
 * - Any other HTTPS remote URL is routed through the backend proxy
 *   `/api/images/proxy?url=...&w=...` so that:
 *     - Heavy CDN images are server-side resized, compressed, and cached.
 *     - Third-party hosts that block hotlinking still load (the request
 *       comes from the backend, not the customer's browser).
 *   The backend enforces SSRF safety (no private IPs, https only).
 * - Non-http(s) sources (blob:, data:, relative paths) are returned as-is.
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

export const optimizedImageUrl = (src, width) => {
  if (!src || typeof src !== 'string') return src;

  // Already-proxied URL (backend rewrites image_url to `/api/images/proxy?url=...`).
  // Just append &w=NNN; do not wrap again.
  if (src.includes('/api/images/proxy')) {
    if (!width) return src;
    const sep = src.includes('?') ? '&' : '?';
    return `${src}${sep}w=${Math.round(width)}`;
  }

  // Internal API: append ?w=. Only treat as internal if it's a same-origin
  // path (relative URL) OR a same-origin absolute URL. A cross-origin URL
  // that happens to include `/api/images/file/` (e.g., from an old
  // deployment domain) must still go through the proxy.
  const isSelfOrigin = (() => {
    try {
      if (!API) return false;
      const u = new URL(src, window.location.origin);
      const a = new URL(API);
      return u.origin === window.location.origin || u.origin === a.origin;
    } catch { return false; }
  })();
  if ((src.startsWith('/api/images/file/')) ||
      (isSelfOrigin && src.includes('/api/images/file/'))) {
    if (!width) return src;
    const sep = src.includes('?') ? '&' : '?';
    return `${src}${sep}w=${Math.round(width)}`;
  }

  // Skip non-http(s) sources (data:, blob:, relative app paths).
  if (!src.startsWith('https://') && !src.startsWith('http://')) return src;

  // Any HTTPS image → proxy for resize, compression and hotlink bypass.
  // Plain http:// is left untouched (backend rejects it anyway).
  if (src.startsWith('https://')) {
    const w = width ? `&w=${Math.round(width)}` : '';
    return `${API}/api/images/proxy?url=${encodeURIComponent(src)}${w}`;
  }

  return src;
};

/**
 * Default props for off-screen images so they don't block the main thread.
 */
export const lazyImgProps = {
  loading: 'lazy',
  decoding: 'async',
};
